// Elevare Business Health Questionnaire — single source of truth for the
// in-app fillable form. The backend receives the answers + a rendered outline
// derived from this schema, so this file is the only place questions live.

export const BLANK_PDF_URL =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_bbb-boost/artifacts/ts2jtfst_Elevare_Business_Health_Questionnaire.pdf";

const FREQ = ["Daily", "Weekly", "Monthly", "Quarterly", "Annually", "Never"];
const EASE = ["Very easily", "Somewhat easily", "With some difficulty", "With great difficulty", "Not at all"];

// type: text | num | date | long | yesno | select | check
export const SECTIONS = [
  {
    id: "s1", title: "Business Profile",
    purpose: "Understand who the client is and establish a baseline.",
    fields: [
      { k: "biz_name", label: "What is the name of your business?", type: "text" },
      { k: "industry", label: "What industry does your business operate in?", type: "text" },
      { k: "years_operating", label: "How many years has your business been operating?", type: "num" },
      { k: "locations", label: "How many locations does your business currently have?", type: "num" },
      { k: "products", label: "What products or services does your business provide?", type: "long" },
      { k: "primary_customer", label: "Who is your primary customer?", type: "text" },
      { k: "geo_area", label: "What geographic area do you primarily serve?", type: "text" },
      { k: "employees", label: "How many employees does your business currently have?", type: "num" },
      { k: "structure", label: "What type of business structure do you operate under?", type: "select",
        choices: ["Sole Proprietorship", "LLC", "Partnership", "S Corporation", "C Corporation", "Other"] },
      { k: "annual_revenue", label: "What is your approximate annual revenue?", type: "num" },
      { k: "rev_pct_primary", label: "What percentage of revenue comes from your primary product or service?", type: "num" },
      { k: "goals_12mo", label: "What are the three biggest goals for your business over the next 12 months?", type: "long" },
    ],
  },
  {
    id: "s2", title: "Financial Health",
    purpose: "Determine whether the business is financially healthy and where money may be leaking.",
    fields: [
      { k: "prev_revenue", label: "Total revenue during the previous fiscal year?", type: "num" },
      { k: "avg_monthly_rev", label: "Average monthly revenue?", type: "num" },
      { k: "prev_net_profit", label: "Approximate net profit during the previous fiscal year?", type: "num" },
      { k: "net_margin", label: "Approximate net profit margin (%)?", type: "num" },
      { k: "avg_monthly_opex", label: "Average monthly operating expenses?", type: "num" },
      { k: "monthly_payroll", label: "Approximate monthly payroll spend?", type: "num" },
      { k: "outstanding_debt", label: "Outstanding business debt currently?", type: "num" },
      { k: "cash_runway_months", label: "Months of operating expenses your cash reserves could cover?", type: "num" },
      { k: "ar_owed", label: "Money currently owed to your business by customers (A/R)?", type: "num" },
      { k: "ap_owed", label: "Money your business currently owes vendors/others (A/P)?", type: "num" },
      { k: "rev_trend_3yr", label: "Revenue over the past three years has…", type: "select",
        choices: ["Increased", "Decreased", "Remained relatively stable"] },
      { k: "has_budget", label: "Do you maintain a formal business budget?", type: "yesno" },
      { k: "review_financials", label: "How often do you review your financial statements?", type: "select", choices: FREQ },
      { k: "does_forecasts", label: "Do you create financial forecasts?", type: "yesno" },
      { k: "biggest_financial_challenge", label: "Biggest financial challenge facing your business right now?", type: "long" },
    ],
  },
  {
    id: "s3", title: "Sales Performance",
    purpose: "Understand how effectively the company turns opportunities into revenue.",
    fields: [
      { k: "leads_month", label: "Approximately how many leads per month?", type: "num" },
      { k: "lead_conv_pct", label: "Approximate % of leads that become paying customers?", type: "num" },
      { k: "avg_txn_value", label: "Average customer transaction or contract value?", type: "num" },
      { k: "lead_to_customer_time", label: "Typical time to turn a lead into a customer?", type: "text" },
      { k: "top_customer_sources", label: "Three largest sources of new customers?", type: "long" },
      { k: "uses_crm", label: "Do you currently use a CRM system?", type: "yesno" },
      { k: "track_leads_how", label: "How do you currently track leads and opportunities?", type: "long" },
      { k: "repeat_pct", label: "% of customers who make repeat purchases or renew?", type: "num" },
      { k: "formal_sales_process", label: "Do you have a formal sales process employees follow?", type: "yesno" },
      { k: "sales_goals", label: "Do you have specific sales goals or quotas?", type: "yesno" },
      { k: "review_sales", label: "How often do you review sales performance?", type: "select", choices: FREQ },
      { k: "biggest_sales_problem", label: "Biggest problem affecting your sales right now?", type: "long" },
    ],
  },
  {
    id: "s4", title: "Marketing & Brand",
    purpose: "Determine how effectively the company attracts potential customers.",
    fields: [
      { k: "marketing_budget", label: "Average monthly marketing budget?", type: "num" },
      { k: "marketing_channels", label: "Which marketing channels do you use? (comma-separated)", type: "check" },
      { k: "top_channel", label: "Which channel generates the most customers?", type: "text" },
      { k: "tracks_roi", label: "Do you track return on marketing investment?", type: "yesno" },
      { k: "has_website", label: "Do you have a professional website?", type: "yesno" },
      { k: "web_traffic", label: "Approximate monthly website traffic?", type: "num" },
      { k: "uses_seo", label: "Do you use search engine optimization (SEO)?", type: "yesno" },
      { k: "social_platforms", label: "Which social platforms are active? (comma-separated)", type: "check" },
      { k: "content_freq", label: "How often do you publish/update marketing content?", type: "select", choices: FREQ },
      { k: "collects_reviews", label: "Do you collect customer reviews?", type: "yesno" },
      { k: "avg_online_rating", label: "Approximate average online rating?", type: "num" },
      { k: "uses_email_text", label: "Do you use email or text marketing?", type: "yesno" },
      { k: "brand_vs_competitors", label: "How would you describe your brand vs competitors?", type: "long" },
      { k: "differentiator", label: "What makes your business different from competitors?", type: "long" },
      { k: "biggest_marketing_challenge", label: "Biggest marketing challenge you currently face?", type: "long" },
    ],
  },
  {
    id: "s5", title: "Operations",
    purpose: "Find inefficiencies, bottlenecks, and areas to become more efficient.",
    fields: [
      { k: "daily_ops_desc", label: "Describe your company's day-to-day operations.", type: "long" },
      { k: "has_sops", label: "Do you have written SOPs for major processes?", type: "yesno" },
      { k: "sop_review_freq", label: "How often are SOPs reviewed or updated?", type: "select", choices: FREQ },
      { k: "top3_processes", label: "Three most important processes to operate your business?", type: "long" },
      { k: "processes_documented", label: "How many of those processes are documented?", type: "num" },
      { k: "owner_involved_tasks", label: "Tasks that require the owner to be personally involved?", type: "long" },
      { k: "time_consuming_tasks", label: "Tasks that consume the most employee time?", type: "long" },
      { k: "delay_causing_processes", label: "Processes that cause the most delays or problems?", type: "long" },
      { k: "tracks_productivity", label: "Do you track employee productivity/efficiency?", type: "yesno" },
      { k: "supply_issues", label: "Regular inventory/supply/scheduling/production problems?", type: "yesno" },
      { k: "handle_25pct", label: "How easily could you handle a 25% increase in customers?", type: "select", choices: EASE },
      { k: "handle_100pct", label: "How easily could you handle a 100% increase in customers?", type: "select", choices: EASE },
      { k: "biggest_ops_problem", label: "Biggest operational problem facing your company?", type: "long" },
    ],
  },
  {
    id: "s6", title: "People & Leadership",
    purpose: "Understand the team, organizational structure, and leadership.",
    fields: [
      { k: "fulltime", label: "How many full-time employees?", type: "num" },
      { k: "parttime", label: "How many part-time employees?", type: "num" },
      { k: "turnover_rate", label: "Approximate annual employee turnover rate (%)?", type: "num" },
      { k: "avg_tenure", label: "Average length of time employees stay?", type: "text" },
      { k: "job_descriptions", label: "Written job descriptions for each position?", type: "yesno" },
      { k: "onboarding_process", label: "Formal employee onboarding process?", type: "yesno" },
      { k: "ongoing_training", label: "Ongoing employee training?", type: "yesno" },
      { k: "perf_evals", label: "Regular performance evaluations?", type: "yesno" },
      { k: "org_structure", label: "Clearly defined organizational structure?", type: "yesno" },
      { k: "managers_authority", label: "Managers given clear responsibilities and authority?", type: "yesno" },
      { k: "owner_dependence", label: "How dependent is the business on the owner? (0–100)", type: "num" },
      { k: "away_30_days", label: "If you were out for 30 days, how well would it operate?", type: "select", choices: EASE },
      { k: "biggest_people_challenge", label: "Biggest employee or leadership challenge?", type: "long" },
    ],
  },
  {
    id: "s7", title: "Customer Experience",
    purpose: "Understand whether the company is creating loyal customers.",
    fields: [
      { k: "measure_satisfaction", label: "How do you measure customer satisfaction?", type: "long" },
      { k: "return_pct", label: "% of customers who return or purchase again?", type: "num" },
      { k: "complaints_month", label: "Approximate customer complaints per month?", type: "num" },
      { k: "complaint_resolution_time", label: "How quickly are complaints typically resolved?", type: "text" },
      { k: "formal_service_process", label: "Formal customer service process?", type: "yesno" },
      { k: "asks_feedback", label: "Do you regularly ask customers for feedback?", type: "yesno" },
      { k: "loyalty_program", label: "Customer loyalty or retention program?", type: "yesno" },
      { k: "customers_praise", label: "What do customers most frequently praise?", type: "long" },
      { k: "customers_complain", label: "What do customers most frequently complain about?", type: "long" },
      { k: "biggest_cx_issue", label: "Biggest customer experience issue you face?", type: "long" },
    ],
  },
  {
    id: "s8", title: "Technology & Innovation",
    purpose: "Find opportunities to save time, reduce costs, and improve through technology.",
    fields: [
      { k: "accounting_software", label: "What accounting software do you use?", type: "text" },
      { k: "crm_system", label: "What CRM system do you use?", type: "text" },
      { k: "pos_system", label: "What point-of-sale system do you use (if any)?", type: "text" },
      { k: "pm_software", label: "What project management / workflow software?", type: "text" },
      { k: "admin_automated_pct", label: "How much daily admin work is automated (%)?", type: "num" },
      { k: "manual_automatable", label: "Manual tasks you believe could be automated?", type: "long" },
      { k: "uses_ai", label: "Does your company currently use AI?", type: "yesno" },
      { k: "ai_how", label: "If yes, how are you using AI?", type: "long" },
      { k: "tech_frustration", label: "What technology frustrates employees most?", type: "long" },
      { k: "tech_infra_rating", label: "Rate your current technology infrastructure.", type: "select",
        choices: ["Excellent", "Good", "Average", "Poor", "Very Poor"] },
      { k: "cybersecurity_process", label: "Formal cybersecurity/data protection process?", type: "yesno" },
      { k: "cyber_training", label: "Do employees receive cybersecurity training?", type: "yesno" },
      { k: "tech_investment_impact", label: "Tech investment that could have the greatest impact?", type: "long" },
    ],
  },
  {
    id: "s9", title: "Strategy & Growth",
    purpose: "Understand where the company is going and whether its strategy can get it there.",
    fields: [
      { k: "long_term_goal", label: "Primary long-term goal?", type: "long" },
      { k: "five_year_vision", label: "Where do you want the business in five years?", type: "long" },
      { k: "competitive_advantage", label: "Primary competitive advantage?", type: "long" },
      { k: "top3_competitors", label: "Three biggest competitors?", type: "long" },
      { k: "competitors_better", label: "What do competitors do better than you?", type: "long" },
      { k: "you_better", label: "What do you do better than competitors?", type: "long" },
      { k: "planning_expansion", label: "Currently planning to expand?", type: "yesno" },
      { k: "expansion_type", label: "If yes, what type of expansion?", type: "long" },
      { k: "new_revenue_streams", label: "New products/services/revenue streams considered?", type: "long" },
      { k: "growth_blockers", label: "What is preventing you from growing faster?", type: "long" },
      { k: "biggest_opportunity", label: "Single biggest opportunity right now?", type: "long" },
      { k: "biggest_risk_3yr", label: "Biggest risk over the next three years?", type: "long" },
      { k: "solve_one_problem", label: "If you could solve one problem immediately, what?", type: "long" },
    ],
  },
  {
    id: "s10", title: "Owner / Leadership Perspective",
    purpose: "Capture information that numbers alone cannot tell us.",
    fields: [
      { k: "does_well", label: "What does your business do exceptionally well?", type: "long" },
      { k: "does_poorly", label: "What does your business do poorly?", type: "long" },
      { k: "keeps_up_at_night", label: "What problem keeps you up at night?", type: "long" },
      { k: "owner_time", label: "What takes up most of your time as owner?", type: "long" },
      { k: "enjoy_least", label: "What part of running the business do you enjoy least?", type: "long" },
      { k: "enjoy_most", label: "What part do you enjoy most?", type: "long" },
      { k: "biggest_frustration", label: "Biggest frustration with your business?", type: "long" },
      { k: "unlimited_resources", label: "What would you change with unlimited resources?", type: "long" },
      { k: "preventing_potential", label: "What's preventing the business from full potential?", type: "long" },
      { k: "personal_success", label: "What would success look like for you personally?", type: "long" },
      { k: "business_success", label: "What would success look like for your business?", type: "long" },
      { k: "why_started", label: "Why did you originally start this business?", type: "long" },
    ],
  },
  {
    id: "s11", title: "Final Elevare Questions",
    purpose: "Transition from assessment into a consulting relationship.",
    fields: [
      { k: "why_consulting_now", label: "Why are you seeking outside consulting right now?", type: "long" },
      { k: "hoping_elevare", label: "What are you hoping Elevare can help you accomplish?", type: "long" },
      { k: "success_relationship", label: "What would make this relationship successful for you?", type: "long" },
      { k: "how_quickly_start", label: "How quickly would you like to begin implementing changes?", type: "text" },
      { k: "major_investment", label: "Considering a major investment in your business?", type: "yesno" },
      { k: "major_changes", label: "Major changes happening we should know about?", type: "long" },
      { k: "anything_else", label: "Anything else we should know before we begin?", type: "long" },
    ],
  },
];

export const SUPPORTING_DOCS = [
  "Profit & Loss Statement",
  "Balance Sheet",
  "Cash Flow Statement",
  "Recent Business Tax Return",
  "Accounts Receivable Report",
  "Accounts Payable Report",
  "Debt Schedule",
  "Payroll Summary",
  "Sales / Revenue Report",
  "CRM or Lead Report",
  "Marketing / Advertising Reports",
  "Website Analytics",
  "Inventory Report (if applicable)",
  "Organizational Chart",
  "Relevant Standard Operating Procedures",
];

// Build the ordered outline (label/value pairs by section) the backend uses to
// render the completed PDF. Skips empty answers.
export function buildRendered(answers) {
  const fmt = (f, v) => {
    if (v === undefined || v === null || v === "") return "";
    if (f.type === "yesno") return v === true || v === "yes" ? "Yes" : v === false || v === "no" ? "No" : String(v);
    return String(v);
  };
  return SECTIONS.map((s) => ({
    section: s.title,
    items: s.fields
      .map((f) => ({ label: f.label, value: fmt(f, answers[f.k]) }))
      .filter((it) => it.value !== ""),
  })).filter((s) => s.items.length > 0);
}
